<template>
    <div class="product-item" @mouseover="checkOverflow">
        <div class="product-item__body">
            <a :href="urlWithinCollection || url" :title="product.title" class="product-item__link">{{ product.title }}</a>
            <div class="product-item__image">
                <img v-if="product.images && product.images.length" :data-src="productImage | imageSize('500x')" :alt="product.title" class="lazyload"/>
                <slot v-else name="placeholder-image"></slot>
                <span v-if="isOutOfStock && source !== 'wishlist'" class="product-badge product-badge--best-seller">{{ translations.out_of_stock }}</span>
                <!-- Desktop: Variant Selector -->
                
                <template v-if="windowWidth >= 992 && !hideVariantSelector && source !== 'wishlist'">
                    <div class="variant-selector" v-if="!isOutOfStock && product.variants && product.variants.length > 0 && product.type != 'Gift Card'">
                        <div v-if="isSubmitting" class="notice-text">{{ translations.adding }}</div>
                        <div v-else-if="!isSubmitting && isAdded" class="notice-text">{{ translations.added }}</div>
                        <form v-else :id="'product-form-' + product.id" @submit.prevent="handleAddToCart">
                            <input name="id" type="hidden" ref="variantInput">
                            <button v-for="variant in product.variants" :key="variant.id" :class="variant.available ? 'available': 'unavailable'" :data-variant-id="variant.id" @mouseover="setCurrentVariant(variant.id)">
                                <span v-if="lookEnabled">{{variant.title}}</span>
                                <span v-else>{{ variant.title | getSizeValue }}</span>
                            </button>
                        </form>
                    </div>
                </template>

                <button v-if="source !== 'wishlist'"
                        @click="addToWishlist" 
                        :data-variant="product.variants[0].id" 
                        :data-product="product.id" 
                        :data-pTitle="product.title" class="add-to-wishlist" :class="{ 'active' : isAddedToWishlist }">
                    <svg xmlns="http://www.w3.org/2000/svg" 
                        width="20" 
                        height="20" viewBox="0 0 24 24" fill="none" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                        <title>Wishlist icon</title>
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                    </svg>
                </button>
            </div>
            <div class="product-item__description">
                <span class="product-item__brand">{{ product.vendor }}</span>

                <div class="product-item__name product-name">{{ product.title }}</div>
                
                <s v-if="isSale" class="price price--old">{{ product.compare_at_price | activeCurrencyFromCents }}</s>
                <span class="price" :class="{ 'price--sale': isSale }">{{ product.price  | activeCurrencyFromCents }}</span>
                <div>
                    <span class="product-item--discount" v-if="isSale">{{ Math.round((product.compare_at_price - product.price) / (product.compare_at_price) * 100 )}} % OFF</span>
                </div>
            </div>

            <div v-if="source === 'wishlist'" class="product-item__wishlist-options">
                <form class="mt-3" 
                    :data-variant="selectedVariant.id" 
                    :data-product="product.id" 
                    @submit.prevent="handleAddToCart"
                >
                    <a class="select d-flex align-items-center" @click.prevent="toggleDropdown">
                        <span v-if="!selectedVariant">Pick a Size</span>
                        <span v-else>{{ selectedVariant.title }}</span>
                    </a>
                    <ul class="product-item__variant-selector" :class="{ 'product-item__variant-selector--active' : isActive }">
                        <li v-for="variant in product.variants"
                            :key="variant.id" 
                            :disabled="!variant.available"
                            :data-value="variant"
                            @click.prevent="setSelectedVariant(variant)"
                        >
                            {{ variant.title }}
                        </li>
                    </ul>

                    <button-spinner :is-loading="isSubmitting" class="button button--wishlist mt-3 w-100" :class="{ 'button--wishlist-disabled': !selectedVariant || !selectedVariant.available }">
                        <span v-if="!selectedVariant || selectedVariant.available">Add to Bag</span>
                        <span v-else-if="!selectedVariant || !selectedVariant.available">Sold out</span>
                    </button-spinner>
                </form>

                <a href="javascript:void(0);" class="d-block mt-3" 
                    :data-variant="selectedVariant.id" 
                    :data-product="product.id" 
                    :data-pTitle="product.title"
                    @click.prevent="addToWishlist"
                >
                    Remove
                </a>
            </div>

            <!-- Mobile: Variant Selector -->
            <template v-if="windowWidth < 992 && !hideVariantSelector && source !== 'wishlist'">
                <button class="button button--secondary button--mobile-plp w-100" @click="toggleVariantSelector" v-show="showAddToBag" v-if="!isOutOfStock && product.variants && product.variants.length > 0 && product.type != 'Gift Card'">{{ translations.add_to_bag }}</button>
                <a href="#" class="button button--secondary button--mobile-plp w-100 SI_trigger" :data-product-data="product | jsonStringify" v-else-if="isOutOfStock">{{ translations.request_restock }}</a>
                <a :href="urlWithinCollection || url" :title="product.title" class="button button--secondary text-center w-100" :data-product-data="product | jsonStringify" v-else>{{ translations.view }}</a>
                <div class="variant-selector" v-show="isVariantSelectorOpen">
                    <div v-if="isSubmitting" class="notice-text">{{ translations.adding }}</div>
                    <div v-else-if="!isSubmitting && isAdded" class="notice-text">{{ translations.added }}</div>
                    <form v-else :id="'product-form-' + product.id" @submit.prevent="handleAddToCart">
                        <input name="id" type="hidden" ref="variantInput">
                        <button v-for="variant in product.variants" :key="variant.id" :class="variant.available ? 'available': 'unavailable'" :data-variant-id="variant.id" @mouseover="setCurrentVariant(variant.id)">
                            <span v-if="lookEnabled">{{variant.title}}</span>
                            <span v-else>{{ variant.title | getSizeValue }}</span>
                        </button>
                    </form>
                </div>
            </template>
        </div>
        <!-- Desktop -->
        <div v-if="moreColor && moreColor.length > 0" class="product-item__colors desktop d-none d-md-flex">
            <span class="color--title">
                {{moreColor.length }} {{ translations.colour}}
            </span>
            <div class="color--selection">
                <a v-for="color in moreColor" :key="color.id" :href="'/products/'+ color.split('|')[2]" :title="color.split('|')[2] | titleCase ">
                    <span :class="(product.handle == color.split('|')[2]) ? 'active' : '' " :style="{backgroundColor: color.split('|')[1] }">
                        {{ color.split('|')[2] | titleCase }}
                    </span>
                </a>
            </div>
        </div>
        <!-- Mobile -->
        <div v-if="moreColor && moreColor.length > 0" class="product-item__colors mobile d-md-none">
            <a :href="urlWithinCollection || url" class="color--title">
                + {{moreColor.length }} {{ translations.colour}}
            </a>
        </div>
    </div>
</template>

<script>
import ButtonSpinner from './button-spinner.vue';
import { app } from '../app.js';
export default {
    props: {
        product: {
            type: Object
        },
        translations: {
            type: Object
        },
        lookEnabled: {
            type: Boolean
        },
        hideVariantSelector: {
            type: Boolean
        },
        source: {
            type: String,
            default: ''
        }
    },
    components: {
        ButtonSpinner
    },
    filters: {
        titleCase: function (value) {
            if(value){
                return value.replace(/-/g, ' ');
            }
        }
    },
    mounted(){
        this.moreColor = this.product.tags.filter(tag => {
            return tag.includes('moreColour|');
        });
        setTimeout(() => {
            this.checkWishlist();
        }, 1000);
    },        
    data() {
        return {
            windowWidth: window.innerWidth,
            isVariantSelectorOpen: false,
            isSubmitting: false,
            isAddedToWishlist: false,
            isAdded: false,
            isActive: false,
            showAddToBag: true,
            currentVariantId: null,
            moreColor: [],
            showMoreColors: false,
            selectedVariant: ''
        }
    },
    computed: {
        isSale() {
            return this.product.compare_at_price && this.product.price < this.product.compare_at_price;
        },
        isOutOfStock() {
            if (typeof this.product.available != 'undefined' && typeof this.product.type != 'undefined') {
                if (!this.product.available && this.product.type != "Gift Card") {
                    return true;
                }
            } else if (typeof this.product.availableForSale != 'undefined' && typeof this.product.productType != 'undefined') {
                if (!this.product.availableForSale && this.product.productType != "Gift Card") {
                    return true;
                }
            }
        },
        fileUrl() {
            return app.fileUrl;
        },
        productFileUrl() {
            return app.fileUrl.substring(0, app.fileUrl.lastIndexOf('files'));
        },
        urlWithinCollection() {
            if(this.$parent.collectionHandle){
                return '/collections/' + this.$parent.collectionHandle + this.product.url;
            }else{
                return this.product.url;
            }
        },
        productImage() {
            if (!this.product.images.length) return null;
            if (typeof this.product.images[0].src === 'undefined' && this.product.images[0]) {
                return this.product.images[0];
            } else {
                return this.productFileUrl + this.product.images[0].src;
            }
            
        }
    },
    created() {
        window.addEventListener('resize', this.handleResize);
        this.handleResize()
        console.log(this.product, 'wishlist option');
    },
    destroyed() {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        addToWishlist(event){
            if(this.isAddedToWishlist) {
                if(this.source === 'wishlist') {
                    this.$emit('removed', {id: this.product.id, event: $(event.target)})
                } else {
                    iwish_remove($(event.target), this.product.variants[0].id.toString())
                }
                this.isAddedToWishlist = false
            } else {
                iwish_add($(event.target), this.product.variants[0].id.toString())
                this.isAddedToWishlist = true
            }
            
        },
        checkWishlist() {
            // setTimeout(() => {
            //     checkIwish($('.product-detail'), this.currentVariant.id.toString());
            // }, 1000);
            let userWishList = localStorage.getItem('iWishlistmain');
            
            if(userWishList) {
                Object.entries(JSON.parse(userWishList)).map(id => {
                    if(id[0] == this.product.id) {
                        this.isAddedToWishlist = id[1].find(variantId => variantId == this.product.variants[0].id) ? true : false
                    }
                })
            }
        },
        toggleColor(){
            this.showMoreColors = !this.showMoreColors;
        },
        toggleDropdown () {
            this.isActive = !this.isActive
        },
        
        handleResize() {
            clearTimeout(this.resizeId)
            this.resizeId = setTimeout(() => {
                this.windowWidth = window.innerWidth      
            }, 1000)
        },
        toggleVariantSelector() {
            this.isVariantSelectorOpen = !this.isVariantSelectorOpen;
            this.showAddToBag = !this.showAddToBag;
            let mobileVariantSelector = this.$el.parentElement.querySelector('.product-item .variant-selector');
            if (mobileVariantSelector) {
                let self = mobileVariantSelector;
                setTimeout(function() {
                    let mobileSelectorWidth = self.offsetWidth;
                    let mobileVariantButtons = self.querySelector('form').children;
                    let mobileButtonsWidth = 0;
                    for (let i = 0; i<mobileVariantButtons.length; i++) {
                        mobileButtonsWidth += mobileVariantButtons[i].offsetWidth;
                    }
                    if (mobileButtonsWidth > mobileSelectorWidth) {
                        mobileVariantSelector.classList.add('has-overflow');
                    }
                    
                }, 300);
            }
            
        },
        checkOverflow() {
            let variantSelector = this.$el.querySelector('.product-item__image .variant-selector');
            
            if (variantSelector) {
                let variantSelectorWidth = variantSelector.offsetWidth;
                let variantButtons = this.$el.querySelector('form').children;
                let buttonsWidth = 0;
                
                for (let i = 0; i<variantButtons.length; i++) {
                    buttonsWidth += variantButtons[i].offsetWidth;
                }
                if (buttonsWidth > variantSelectorWidth) {
                    variantSelector.classList.add('has-overflow');
                }
            }
        },
        setCurrentVariant(id) {
            this.$refs.variantInput.value = id;
            this.currentVariantId = id;
        },
        setSelectedVariant(variant) {
            this.selectedVariant = variant
            this.isActive = false
        },
        handleAddToCart(event) {
          this.isSubmitting = true;
          
          if (this.currentVariantId || this.selectedVariant) {
              this.isSubmitting = true;
              const id = this.source === 'wishlist' ? this.selectedVariant.id : this.currentVariantId
              app.addToCart(id, 1).then((response) => {
                this.isSubmitting = false;
                this.isAdded = true;
                setTimeout(() => {
                    this.isAdded = false;
                },2000);
                if(this.source === 'wishlist') {
                    this.$emit('removed', {id: this.product.id, event: $(event.target)})
                }
              }).catch((error) => {
                  console.log('error', error);
                  this.isSubmitting = false;
                  alert('Cannot reach the server at this time, please try again later.');
              });
          }
      }
    }
}
</script>